import { useMemo } from 'react';
import _ from 'lodash';

import { useGetPatientQuery } from '../api/patientApi';
import { meetsProfileDependency } from '../helpers/logic_helpers';
import {
  eventCompletedDate,
  eventIsActive,
  eventPhaseIsActive,
  eventSchedule,
  patientProgramPhase,
  repeatEventIsActive,
} from '../helpers/appevent_helpers';
import { AppEvent } from '../types/ApplicationEvent';
import { CompletedEvent, ProfileKeysById } from '../types/Patient';
import { useGetApplicationEventsQuery } from '../api/applicationEventApi';

import usePatientDates, { PatientDatesInMS } from './usePatientDates';

const eventMeetsProfile = (event: AppEvent, profile: ProfileKeysById) => {
  let meetsProfile = true;

  const profileDependency = event.profileDependencies;

  meetsProfile = meetsProfileDependency(
    profileDependency,
    event.profileDependencyType,
    profile,
  );

  return meetsProfile;
};

const maternityEventsByDeliveryStatus = (
  events: AppEvent[],
  phase: string,
  confirmedSurgery: boolean,
) => {
  if (phase === 'inPeriop' && !confirmedSurgery) {
    // if the patient is in hospital but baby was not delivered yet
    // i.e. do not show post-partum events
    return _.reject(events, ['inPreop', false]);
  }

  if (phase === 'inPeriop' && confirmedSurgery) {
    // if the baby was delivered remove events that start in preop
    // i.e. do not show pregnancy events
    return _.reject(events, ['inPreop', true]);
  }

  return events;
};

// adds startDateTime and endDateTime to event object
// adds completedDate and done boolean to event object
const addScheduleToEvent = (
  events: AppEvent[],
  dates: PatientDatesInMS,
  completedEvents: CompletedEvent[],
) => {
  const eventsWithSchedule = events.map((event) => {
    const { startDateTime, endDateTime } = eventSchedule(event, dates);
    let completedDate = null;

    if (startDateTime) {
      completedDate = eventCompletedDate(event, startDateTime, completedEvents);
    }

    const eventCopy = {
      ...event,
      startDateTime,
      endDateTime,
      completedDate,
      done: !_.isEmpty(completedDate),
    };
    return eventCopy;
  });

  return eventsWithSchedule;
};

export default function useAppEvents(options = { skip: false }) {
  const { data: patient } = useGetPatientQuery(undefined, { skip: options.skip });
  const { data: applicationEvents, isLoading } = useGetApplicationEventsQuery(undefined, {
    refetchOnMountOrArgChange: 3600,
    skip: options.skip
  });

  const { patientDates, patientDatesInMS } = usePatientDates({ skip: options.skip });

  const timezone = useMemo(() => {
    if (!patient) return null;
    return patient.timezone;
  }, [patient]);

  const completedEvents = useMemo(() => {
    if (!patient) return [];
    return patient.completedEvents;
  }, [patient]);

  const programPhase = useMemo(() => {
    if (!patient || !patientDates || !timezone) return undefined;

    const { confirmedSurgery, daysPostOp, hasPeriop } = patient;
    const requireSurgeryConfirm = hasPeriop ? false : !confirmedSurgery;

    const phase = patientProgramPhase(patientDates, daysPostOp, requireSurgeryConfirm);
    return phase;
  }, [patient, patientDates, timezone]);

  const profile = useMemo(() => {
    if (!patient) return null;
    return patient.profile;
  }, [patient]);

  const clinicalContact = useMemo(() => {
    if (!patient) return null;
    return patient.clinicalContact;
  }, [patient]);

  const isMaternityProgram = useMemo(() => {
    if (!patient) return null;
    const { maternityProgram, confirmedSurgery } = patient;

    if (!maternityProgram) return null;
    return { confirmedSurgery };
  }, [patient]);

  const eventsForProfile = useMemo(() => {
    if (!profile || !applicationEvents?.messageEvents) return null;

    let {
      messageEvents = [],
      todoEvents = [],
      metricEvents = [],
      trackerEvents = [],
    } = applicationEvents;

    messageEvents = messageEvents
      .filter((event) => eventMeetsProfile(event, profile));

    todoEvents = todoEvents
      .filter((event) => eventMeetsProfile(event, profile));

    metricEvents = metricEvents
    .filter((event) => eventMeetsProfile(event, profile));

    trackerEvents = trackerEvents
    .filter((event) => eventMeetsProfile(event, profile));

    return {
      messageEvents,
      todoEvents,
      metricEvents,
      trackerEvents,
    };
  }, [applicationEvents, profile]);

  const eventsWithSchedule = useMemo(() => {
    if (!eventsForProfile || !patientDatesInMS) return null;

    const {
      messageEvents,
      todoEvents,
      metricEvents,
      trackerEvents,
    } = eventsForProfile;

    return {
      messageEvents: addScheduleToEvent(messageEvents, patientDatesInMS, completedEvents),
      todoEvents: addScheduleToEvent(todoEvents, patientDatesInMS, completedEvents),
      metricEvents: addScheduleToEvent(metricEvents, patientDatesInMS, completedEvents),
      trackerEvents: addScheduleToEvent(trackerEvents, patientDatesInMS, completedEvents),
    };
  }, [completedEvents, eventsForProfile, patientDatesInMS]);

  const unread = useMemo(() => {
    if (!eventsWithSchedule || !programPhase || !timezone) {
      return [];
    }

    const { messageEvents } = eventsWithSchedule;

    const unreadMessages = messageEvents
      .filter((event) => (_.isEmpty(event.completedDate) && eventPhaseIsActive(event, programPhase)))
      .filter((event) => (event.repeating ? repeatEventIsActive(event, timezone) : eventIsActive(event)));

    if (!_.isEmpty(isMaternityProgram)) {
      const { confirmedSurgery } = isMaternityProgram;
      return maternityEventsByDeliveryStatus(unreadMessages, programPhase, confirmedSurgery);
    }

    return unreadMessages;
  }, [eventsWithSchedule, programPhase, timezone, isMaternityProgram]);

  const activeMetrics = useMemo(() => {
    if (!eventsWithSchedule || !programPhase || !timezone) {
      return [];
    }

    const { metricEvents } = eventsWithSchedule;

    const activeMetrics = metricEvents
      .filter((event) => (_.isEmpty(event.completedDate) && eventPhaseIsActive(event, programPhase)))
      .filter((event) => (event.repeating ? repeatEventIsActive(event, timezone) : eventIsActive(event)));

    if (!_.isEmpty(isMaternityProgram)) {
      const { confirmedSurgery } = isMaternityProgram;
      return maternityEventsByDeliveryStatus(activeMetrics, programPhase, confirmedSurgery);
    }

    return activeMetrics;
  }, [eventsWithSchedule, programPhase, timezone, isMaternityProgram]);

  const activeTrackers = useMemo(() => {
    if (!eventsWithSchedule || !programPhase || !timezone) {
      return [];
    }

    const { trackerEvents } = eventsWithSchedule;

    const activeTrackers = trackerEvents
      .filter((event) => (_.isEmpty(event.completedDate) && eventPhaseIsActive(event, programPhase)))
      .filter((event) => (event.repeating ? repeatEventIsActive(event, timezone) : eventIsActive(event)));

    if (!_.isEmpty(isMaternityProgram)) {
      const { confirmedSurgery } = isMaternityProgram;
      return maternityEventsByDeliveryStatus(activeTrackers, programPhase, confirmedSurgery);
    }

    return activeTrackers;
  }, [eventsWithSchedule, programPhase, timezone, isMaternityProgram]);

  const activeTodos = useMemo(() => {
    if (!eventsWithSchedule || !programPhase || !timezone) {
      return [];
    }

    const { todoEvents } = eventsWithSchedule;

    const todoList = todoEvents
      .filter((event) => (eventPhaseIsActive(event, programPhase)))
      .filter((event) => (event.repeating ? repeatEventIsActive(event, timezone) : eventIsActive(event)));

    if (!_.isEmpty(isMaternityProgram)) {
      const { confirmedSurgery } = isMaternityProgram;
      return maternityEventsByDeliveryStatus(todoList, programPhase, confirmedSurgery);
    }

    return todoList.sort((a, b) => Number(a.done) - Number(b.done));
  }, [eventsWithSchedule, programPhase, timezone, isMaternityProgram]);

  const todoCount = useMemo(() => {
    if (!activeTodos) return 0;
    return activeTodos.filter((tasks) => !tasks.done).length;
  }, [activeTodos]);

  const futureTodos = useMemo(() => {
    if (!eventsWithSchedule || !programPhase) return [];
    const { todoEvents } = eventsWithSchedule;

    const futureList = todoEvents
      .filter((event) => eventPhaseIsActive(event, programPhase))
      .filter((event) => !event.repeating
      && event.startDateTime
      && (_.now() < event.startDateTime));

    if (!_.isEmpty(isMaternityProgram)) {
      const { confirmedSurgery } = isMaternityProgram;
      return maternityEventsByDeliveryStatus(futureList, programPhase, confirmedSurgery);
    }

    return futureList;
  }, [eventsWithSchedule, isMaternityProgram, programPhase]);

  return {
    activeTodos,
    clinicalContact,
    completedEvents,
    eventsWithSchedule,
    futureTodos,
    isLoading,
    programPhase,
    todoCount,
    unread,
    activeMetrics,
    activeTrackers,
  };
}
